"use client";

// Renders a blog cover image with a graceful fallback to the per-category
// BlogThumbnail. Missing image URLs already fall back at the call site, but
// CMS-supplied URLs that 404 or otherwise fail to load only surface as a
// browser image-load error — this listens for that and swaps to the
// category thumbnail instead of leaving a blank gradient behind.
//
// The SSR'd <img>/<Image> tag starts loading as soon as the browser parses
// the HTML, before React hydrates. A fast failure (e.g. DNS error on a
// placeholder domain) can fire its native "error" event before the
// onError listener is attached, so it's missed. The mount-time check below
// catches that already-failed state; onError still covers failures that
// happen after hydration.
import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import BlogThumbnail from "@/components/ui/BlogThumbnail";

interface Props {
  readonly image?: string | null;
  readonly category: string;
  readonly isApiImage: boolean;
  readonly sizes: string;
  readonly priority?: boolean;
}

export default function BlogCoverImage({ image, category, isApiImage, sizes, priority }: Props) {
  const [failed, setFailed] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const el = imgRef.current;
    if (el && el.complete && el.naturalWidth === 0) {
      setFailed(true);
    }
  }, [image]);

  if (image && !failed) {
    return isApiImage ? (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        ref={imgRef}
        src={image}
        alt=""
        loading={priority ? "eager" : "lazy"}
        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
        onError={() => setFailed(true)}
      />
    ) : (
      <Image
        ref={imgRef}
        src={image}
        alt=""
        fill
        sizes={sizes}
        priority={priority}
        className="object-cover transition-transform duration-500 group-hover:scale-[1.03]"
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <>
      <BlogThumbnail category={category} />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            "radial-gradient(color-mix(in oklab, var(--ink) 10%, transparent) 1px, transparent 1px)",
          backgroundSize: "16px 16px",
        }}
      />
    </>
  );
}
